import React from "react";
import { Box, Typography } from "@mui/material";
import { CustomButton, CustomModal } from "@ippon/shared-components";
import MakeContractModal from "../../../../../components/PropertyDetail/PrivateArea/modal/Makecontract";
import EditContractModal from "../../../../../components/PropertyDetail/PrivateArea/modal/Editcontract";
import DepartureProcessModal from "../../../../../components/PropertyDetail/PrivateArea/modal/DepartureProcess";
import { Can } from "@/components/auth/Can";

const normalizeArray = <T,>(v: T | T[] | undefined | null): T[] =>
  Array.isArray(v) ? v : v ? [v] : [];

interface View {
  propertyName: string;
  privateAreaName: string;
  address: string;
  contractorType: string;
  tenantLastName: string;
  tenantFirstName: string;
  tenantPhone: string;
  tenantEmail: string;
  tenantName: string;
  companyName: string;
  representativeLastName: string;
  representativeFirstName: string;
  companyPhone: string;
  companyEmail: string;
  startDate: string;
  endDate: string;
  monthlyRent: number | string;
  pricingType?: "fixed" | "unit_quantity";
  unitPrice?: number | string;
  quantity?: number | string;
  quantityUnit?: string;
  rentUnitPrice: number | string;
  maintenanceFee: number | string;
  maintenanceFeeUnitPrice: number | string;
  deposit: number | string;
  keyMoney: number | string;
  remarks: string;
}

export interface DetailPageModalsProps {
  view: View;
  current: any;
  isAncillary: boolean;
  areaTsubo?: number;

  // NewContract
  isNewContractOpen: boolean;
  setIsNewContractOpen: (v: boolean) => void;
  handleNewContractSubmit: (data: any) => void;
  /** 「既存顧客から」導線で選んだ顧客の契約者プリフィル (新規契約モーダル initialData) */
  newContractInitialData?: Record<string, unknown>;

  // EditContract (契約更新)
  isEditContractOpen: boolean;
  setIsEditContractOpen: (v: boolean) => void;
  handleEditContractSubmit: (data: any) => void;

  // DepartureProcess
  isDepartureProcessOpen: boolean;
  setIsDepartureProcessOpen: (v: boolean) => void;
  handleDepartureProcessSubmit: (data: any) => void;

  // EditContractHistory
  isEditContractHistoryOpen: boolean;
  setIsEditContractHistoryOpen: (v: boolean) => void;
  editingContractData: any;
  setEditingContractData: (v: any) => void;
  handleEditContractHistorySubmit: (data: any) => void;

  // DeleteDocument confirmation
  openDeleteDocModal: boolean;
  setOpenDeleteDocModal: (v: boolean) => void;
  deleteDoc: { url: string; name: string } | null;
  handleConfirmDeleteDocument: () => void;
}

/**
 * 部屋詳細ページで使用する各種モーダル群をまとめたコンポーネント。
 *
 * 親側 (`pages/properties/PrivateArea/Detail/index.tsx`) のレンダリング階層を
 * 浅くすることが目的で、内部ロジックは持たない。
 *
 * B7 (C-8) で切り出し。
 */
const DetailPageModals: React.FC<DetailPageModalsProps> = ({
  view,
  current,
  isAncillary,
  areaTsubo,
  isNewContractOpen,
  setIsNewContractOpen,
  handleNewContractSubmit,
  newContractInitialData,
  isEditContractOpen,
  setIsEditContractOpen,
  handleEditContractSubmit,
  isDepartureProcessOpen,
  setIsDepartureProcessOpen,
  handleDepartureProcessSubmit,
  isEditContractHistoryOpen,
  setIsEditContractHistoryOpen,
  editingContractData,
  setEditingContractData,
  handleEditContractHistorySubmit,
  openDeleteDocModal,
  setOpenDeleteDocModal,
  deleteDoc,
  handleConfirmDeleteDocument,
}) => {
  return (
    <>
      {/* 新規契約作成モーダル */}
      <MakeContractModal
        isOpen={isNewContractOpen}
        onClose={() => setIsNewContractOpen(false)}
        propertyName={view.propertyName}
        propertyAddress={view.address}
        onSubmit={handleNewContractSubmit}
        initialData={newContractInitialData as any}
        isAncillary={isAncillary}
        areaTsubo={areaTsubo}
        // 既存顧客から登録する導線 (プリフィルあり) では契約者情報欄をロックする。
        lockContractorInfo={!!newContractInitialData}
      />

      {/* 契約更新モーダル */}
      <EditContractModal
        isOpen={isEditContractOpen}
        onClose={() => setIsEditContractOpen(false)}
        contractorType={view.contractorType as "individual" | "corporate"}
        initialData={
          {
            contractorType: view.contractorType as "individual" | "corporate",
            tenantLastName: view.tenantLastName,
            tenantFirstName: view.tenantFirstName,
            tenantPhone: view.tenantPhone,
            tenantEmail: view.tenantEmail,
            companyName: view.companyName,
            representativeLastName: view.representativeLastName,
            representativeFirstName: view.representativeFirstName,
            companyPhone: view.companyPhone,
            companyEmail: view.companyEmail,
            startDate: view.startDate,
            endDate: view.endDate,
            contract_form: (() => {
              const contracts = normalizeArray(current?.contractor_details);
              const latest = contracts[contracts.length - 1] as any;
              return latest?.contract_form || "";
            })(),
            maintenanceFee:
              view.maintenanceFee == null || view.maintenanceFee === ""
                ? ""
                : String(view.maintenanceFee),
            rentStartDate: (current as any)?.rent_start_date || "",
            cancellationDate: (() => {
              const contracts = normalizeArray(current?.contractor_details);
              const latest = contracts[contracts.length - 1] as any;
              return latest?.cancellation_date || "";
            })(),
            depositAfter:
              (current as any)?.security_deposit_after == null ||
              (current as any)?.security_deposit_after === ""
                ? ""
                : String((current as any).security_deposit_after),
            monthlyRent:
              view.monthlyRent == null || view.monthlyRent === ""
                ? ""
                : String(view.monthlyRent),
            pricingType: view.pricingType || "fixed",
            unitPrice:
              view.unitPrice == null || view.unitPrice === ""
                ? ""
                : String(view.unitPrice),
            quantity:
              view.quantity == null || view.quantity === ""
                ? "1"
                : String(view.quantity),
            quantityUnit: view.quantityUnit || "台",
            rentUnitPrice:
              view.rentUnitPrice == null || view.rentUnitPrice === ""
                ? ""
                : String(view.rentUnitPrice),
            maintenanceFeeUnitPrice:
              view.maintenanceFeeUnitPrice == null ||
              view.maintenanceFeeUnitPrice === ""
                ? ""
                : String(view.maintenanceFeeUnitPrice),
            deposit:
              view.deposit == null || view.deposit === ""
                ? ""
                : String(view.deposit),
            keyMoney:
              view.keyMoney == null || view.keyMoney === ""
                ? ""
                : String(view.keyMoney),
            note: view.remarks,
            otherFees: Array.isArray((current as any)?.other_fees)
              ? (current as any).other_fees.map((f: any) => ({
                  label: f.label || "",
                  amount: f.amount != null ? String(f.amount) : "",
                }))
              : [],
          } as any
        }
        onSubmit={handleEditContractSubmit}
        isAncillary={isAncillary}
        areaTsubo={areaTsubo}
      />

      {/* 退去処理モーダル */}
      <DepartureProcessModal
        isOpen={isDepartureProcessOpen}
        onClose={() => setIsDepartureProcessOpen(false)}
        tenantName={view.tenantName}
        contractStartDate={view.startDate}
        contractEndDate={view.endDate}
        onSubmit={handleDepartureProcessSubmit}
      />

      {/* 契約情報編集モーダル */}
      <MakeContractModal
        isOpen={isEditContractHistoryOpen}
        onClose={() => {
          setIsEditContractHistoryOpen(false);
          setEditingContractData(null);
        }}
        propertyName={view.propertyName}
        propertyAddress={view.address}
        mode="edit"
        initialData={editingContractData}
        onSubmit={handleEditContractHistorySubmit}
        isAncillary={isAncillary}
        areaTsubo={areaTsubo}
      />

      <CustomModal
        title="書類の削除"
        openModal={openDeleteDocModal}
        handleCloseModal={() => setOpenDeleteDocModal(false)}
        bodyText=""
        hideCloseButton
        modalType="deleteModal"
      >
        <Box>
          <Typography sx={{ fontSize: "14px", color: "#666" }}>
            {deleteDoc?.name || "この書類"} を削除してもよろしいですか？
          </Typography>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "center", gap: "10px" }}>
          <CustomButton
            label="戻る"
            buttonCategory="cancelModal"
            onClick={() => setOpenDeleteDocModal(false)}
          />
          <Can action="document:delete">
            <CustomButton
              label="削除"
              onClick={handleConfirmDeleteDocument}
              buttonCategory="deleteModal"
            />
          </Can>
        </Box>
      </CustomModal>
    </>
  );
};

export default DetailPageModals;
